import React, { forwardRef, useCallback, useImperativeHandle, useState, useEffect, useRef } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { useResponsiveDesign } from '../../../responsiveDesign/responsiveDesign';
import { StepperProgressBar } from './components/StepperProgressBar';
import { StepperContent } from './components/StepperContent';
import { StepNavFooter } from './components/StepNavFooter';
import { StepNavContext, type StepNav, type StepNavSetter } from './stepNavContext';
import { qaAttr } from '@/presentation/utils/qaAttr';

export type AnimationType = 'fade' | 'slide' | 'none';

export interface StepConfig {
  // Stable key used by StepperContent to identify this step's DOM node.
  // Prevents React from reusing nodes when the steps array length changes dynamically,
  // which would cause CSS fade transitions to fire on the wrong content.
  id?: string;
  content: React.ReactNode;
  keepAlive?: boolean;
  canGoNext?: boolean;
  canGoBack?: boolean;
  // Static nav defaults for this step (labels, hideNext). Reactive fields
  // (onNext, canGoNext, dynamic labels) should come from useRegisterStepNav.
  nav?: StepNav;
}

export interface ProgressProps {
  showProgress?: boolean;
  changeStepOnClick?: boolean;
  progressInactiveColor?: string;
  progressActiveColor?: string;
  progressCompletedColor?: string;
  backgroundColor?: string;
}

export interface ExpauseStepperProps {
  progressProps?: ProgressProps;
  initialStep?: number;
  animationType?: AnimationType;
  onStepChanged?: (stepIndex: number) => void;
  steps: StepConfig[];
}

export interface ExpauseStepperRef {
  goNext: () => boolean;
  goPrevious: () => boolean;
  goToStep: (index: number) => boolean;
  getCurrentStep: () => number;
}

export const ExpauseStepper = forwardRef<ExpauseStepperRef, ExpauseStepperProps>(
  (
    {
      progressProps,
      initialStep = 0,
      animationType = 'none',
      onStepChanged,
      steps,
    },
    ref
  ) => {
    const { theme } = useTheme();
    const responsive = useResponsiveDesign();
    const [registeredNav, setRegisteredNav] = useState<StepNav | null>(null);
    const setNav: StepNavSetter = useCallback((nav) => setRegisteredNav(nav), []);

    const showProgress = progressProps?.showProgress ?? false;
    const changeStepOnClick = progressProps?.changeStepOnClick ?? false;
    const progressInactiveColor = progressProps?.progressInactiveColor ?? theme.stepper.progressInactive;
    const progressActiveColor = progressProps?.progressActiveColor ?? theme.stepper.progressActive;
    const progressCompletedColor = progressProps?.progressCompletedColor ?? theme.stepper.progressCompleted;
    const progressBackgroundColor = progressProps?.backgroundColor ?? theme.background;

    const [currentStep, setCurrentStep] = useState(
      Math.min(Math.max(0, initialStep), steps.length - 1)
    );
    const [direction, setDirection] = useState<'forward' | 'backward'>('forward');
    const previousStepRef = useRef(currentStep);

    useEffect(() => {
      if (previousStepRef.current !== currentStep) {
        onStepChanged?.(currentStep);
        previousStepRef.current = currentStep;
      }
    }, [currentStep, onStepChanged]);

    const goNext = (): boolean => {
      if (currentStep >= steps.length - 1) return false;

      const currentStepConfig = steps[currentStep];
      if (currentStepConfig?.canGoNext === false) return false;

      setDirection('forward');
      setCurrentStep((prev) => prev + 1);
      return true;
    };

    const goPrevious = (): boolean => {
      if (currentStep <= 0) return false;

      const currentStepConfig = steps[currentStep];
      if (currentStepConfig?.canGoBack === false) return false;

      setDirection('backward');
      setCurrentStep((prev) => prev - 1);
      return true;
    };

    const goToStep = (index: number): boolean => {
      if (index < 0 || index >= steps.length || index === currentStep) return false;

      setDirection(index > currentStep ? 'forward' : 'backward');
      setCurrentStep(index);
      return true;
    };

    const getCurrentStep = (): number => currentStep;

    useImperativeHandle(ref, () => ({
      goNext,
      goPrevious,
      goToStep,
      getCurrentStep,
    }));

    const containerStyle: React.CSSProperties = {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      width: '100%',
    };

    const progressContainerStyle: React.CSSProperties = {
      position: 'sticky',
      top: 0,
      backgroundColor: progressBackgroundColor,
      zIndex: 10,
      marginBottom: `${responsive.spacing.base}px`,
    };

    const staticNav = steps[currentStep]?.nav;
    const hasNav = staticNav != null || registeredNav != null;
    const activeNav: StepNav = {
      ...(staticNav ?? {}),
      ...(registeredNav ?? {}),
    };

    return (
      <StepNavContext.Provider value={setNav}>
        <div {...qaAttr({ id: 'stepper', value: currentStep })} style={containerStyle}>
          {showProgress && (
            <div style={progressContainerStyle}>
              <StepperProgressBar
                stepCount={steps.length}
                currentStep={currentStep}
                direction={direction}
                progressInactiveColor={progressInactiveColor}
                progressActiveColor={progressActiveColor}
                progressCompletedColor={progressCompletedColor}
                onStepClick={changeStepOnClick ? goToStep : undefined}
              />
            </div>
          )}

          <StepperContent
            steps={steps}
            currentStep={currentStep}
            animationType={animationType}
          />

          {hasNav && (
            <StepNavFooter
              nav={activeNav}
              onPreviousDefault={goPrevious}
            />
          )}
        </div>
      </StepNavContext.Provider>
    );
  }
);

ExpauseStepper.displayName = 'ExpauseStepper';
